// utils/duckdbCsvRegistrar.js

/**
 * Universal DuckDB CSV Table Registrar
 * Handles:
 * - ISO dates
 * - bigint milliseconds
 * - bigint seconds
 * - varchar timestamps
 * - timestamp
 * - date
 * - empty sample
 */

export const convertRowsToCSV = (columns, rows) => {
  if (!rows?.length) return '';

  const header = columns.join(',');

  const csvRows = rows.map((row) =>
    columns
      .map((col) => {
        const value = row[col];

        if (value === null || value === undefined) return '';

        // Convert JS millisecond timestamps to ISO
        if (typeof value === 'number' && String(value).length === 13) {
          const d = new Date(value);
          return d.toISOString();
        }

        if (typeof value === 'string') {
          return `"${value.replace(/"/g, '""')}"`;
        }

        return value;
      })
      .join(',')
  );

  return [header, ...csvRows].join('\n');
};



const normalizeDateColumns = async (tableName, executeQuery) => {
  const schema = await executeQuery(`DESCRIBE ${tableName};`);

  await schema.reduce(
    (chain, col) =>
      chain.then(async () => {
        const colName = col.column_name;
        const colType = col.column_type?.toUpperCase();

        if (!colName || !colType) return;

        const lower = colName.toLowerCase();
        const isDateLike =
          lower.includes('date') || lower.includes('dob') || lower.includes('time');

        if (!isDateLike) return;

        try {
          let alterSQL = null;

          if (colType === 'DATE') {
            console.log(`⏭ ${tableName}.${colName} already DATE`);
            return;
          }

          if (colType.includes('TIMESTAMP')) {
            alterSQL = `
            ALTER TABLE ${tableName}
            ALTER COLUMN ${colName}
            TYPE DATE
            USING CAST(
              date_trunc('day', ${colName})
              AS DATE
            );
          `;
          } else if (colType.includes('BIGINT') || colType.includes('INTEGER')) {
            alterSQL = `
            ALTER TABLE ${tableName}
            ALTER COLUMN ${colName}
            TYPE DATE
            USING CAST(
              TO_TIMESTAMP(
                CASE
                  WHEN ${colName} > 100000000000
                    THEN ${colName} / 1000
                  ELSE ${colName}
                END
              )
              AS DATE
            );
          `;
          } else if (colType.includes('VARCHAR')) {
            alterSQL = `
            ALTER TABLE ${tableName}
            ALTER COLUMN ${colName}
            TYPE DATE
            USING TRY_CAST(${colName} AS DATE);
          `;
          }

          if (alterSQL) {
            await executeQuery(alterSQL);
            console.log(`🗓 Normalized ${tableName}.${colName}`);
          }
        } catch (err) {
          console.warn(`⚠️ Could not normalize ${tableName}.${colName}`);
        }
      }),
    Promise.resolve()
  );
};



export const registerTablesWithDuckDB = async ({
  sources,
  targetSchema,
  isInitialized,
  executeQuery,
  registerFile,
}) => {
  if (!isInitialized) return;

  try {
    const registerSingleTable = async (tableName, columns, rows) => {
      if (!tableName || !rows?.length) return;

      const fileName = `${tableName}.csv`;
      const csvContent = convertRowsToCSV(columns, rows);

      await registerFile(fileName, csvContent);

      // await executeQuery(`DROP TABLE IF EXISTS ${tableName};`);

      await executeQuery(`
        CREATE  OR REPLACE TABLE ${tableName} AS
        SELECT *
        FROM read_csv_auto(
          '${fileName}',
          HEADER = TRUE,
          AUTO_DETECT = TRUE,
          SAMPLE_SIZE = -1,
          ALL_VARCHAR = FALSE,
          IGNORE_ERRORS = TRUE
        );
      `);

      await normalizeDateColumns(tableName, executeQuery);

      console.log(`✅ ${tableName} registered`);
    };

    await (sources || [])
      .filter((src) => src?.name && src?.sampleRows?.length)
      .reduce(
        (p, src) =>
          p.then(() =>
            registerSingleTable(src.name, src.columns, src.sampleRows)
          ),
        Promise.resolve()
      );

    const targets = Array.isArray(targetSchema)
      ? targetSchema
      : [targetSchema];

    await (targets || [])
      .filter((tgt) => tgt?.tableName && tgt?.sampleRows?.length)
      .reduce(
        (p, tgt) =>
          p.then(() => {
            const columns = tgt.columns.map((c) => c.column_name);
            return registerSingleTable(
              tgt.tableName,
              columns,
              tgt.sampleRows
            );
          }),
        Promise.resolve()
      );

    const tables = await executeQuery(`SHOW TABLES;`);
    console.log("📦 Registered Tables:", tables);

  } catch (err) {
    console.error("❌ DuckDB CSV Registration Failed:", err);
  }
};